var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductRowLayoutEnum, ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';
import { Text } from '@ingka/common-styles-webc';
import { informationCircle, registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { Image as SkapaImage } from '@ingka/image-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { Tabs as SkapaTabs } from '@ingka/tabs-webc/passive.js';
import PrintableContentEnum from '../../enums/PrintableContentEnum.js';
import SummaryPageTabEnum from '../../enums/SummaryPageTabEnum.js';
import SummaryPageCustomTabsUtility from '../../utilities/SummaryPageCustomTabsUtility.js';
import SummaryPageImages from '../summary-page-images/SummaryPageImages.js';
import SummaryPageProductList from '../summary-page-product-list/SummaryPageProductList.js';
import SummaryPageSecurity from '../summary-page-security/SummaryPageSecurity.js';
import SummaryPageTabsStyle from './SummaryPageTabsStyle.js';
/**
 * @event storeSelectorChange Triggered when the selected store changes.
 * @event sortListChange Triggered when the selected way of sorting changes.
 * @event modalOpen Triggered when a sheet is opened.
 * @event modalClose Triggered when the user closes a sheet.
 * @event tabChange Triggered when the selected tab changes.
 * @event selectedImageIndexChange Triggered when the selected image changes in the "Images" tab.
 * @event collectProduct Triggered when a product is collected.
 * @event homeDeliveryClick User clicks on the home delivery link to select zip code.
 * @event productListAdd Triggered when the add button is clicked on a product in product list.
 * @event productListRemove Triggered when the remove button is clicked on a product in product list.
 * @event productListExpand Triggered when included shopping products in product list gets expanded.
 */
export default class SummaryPageTabs extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // A list of store availability objects.
        this.storeAvailabilities = null;
        // A list of stores, when left empty the store selector will be hidden
        this.stores = null;
        // The storeId of the selected store
        this.storeId = null;
        // Controls how measurements should be shown in the Product list tab
        this.productMeasures = ProductRowProductMeasureEnum.none;
        // Disables outgoing links. Should be true for kiosks.
        this.disableLinks = false;
        // The currently visible sheet.
        this.visibleModal = null;
        // A list of images used in the "Images" tab
        this.images = null;
        // An object with information for the security tab
        this.securityInformation = null;
        // A collection of props used for the home delivery availability in product list. For more info about the props check the HomeDelivery component.
        this.homeDelivery = null;
        // Zip code will be passed down to home delivery.
        this.zipCode = null;
        // Whether to show the collect checkbox in the product list or not.
        this.collectableProducts = false;
        // A list of product ids that are collected.
        this.collectedProducts = null;
        // Show text describing the color and/or material of the product.
        this.showDesignText = false;
        // A list of sections used in the product list.
        this.productListSections = null;
        // Defines optional custom tab buttons. Up to five tabs are supported.
        this.customTabButtons = null;
        // Highest heading on the Summary page will be h2 as opposed to h1. Set to true if planner is e.g. used in an iframe with an h1 header outside of the planner.
        this.startHeadingsAtH2 = false;
        // A list of included products within SPR products. Must be set (if applicable) for total package weight.
        this.includedProducts = null;
        // A list of product item numbers that can be added in the product list.
        this.productListAddable = null;
        // A list of product item numbers that can be removed in the product list.
        this.productListRemovable = null;
        // Sets the default sorting of the product list.
        this.defaultSortBy = null;
        // A sorted list of content to include in the print layout. When not set, the currently shown tab is printed. To include custom content in the print, the content must also be included as the slot "custom-print-content". E.g. ['productListTab', 'imagesTab', 'securityTab', 'customPrintContent'].
        this.contentToPrint = null;
        // Layout for summary page. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print. Make sure SPR's has their child articles in includedProducts, since SPR's are not visible in compactPrint.
        this.pickingListPrintLayout = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        // Auto id for the tabs component
        this.tabsAutoId = 'summary-page-tabs';
        // Keeps state of selected tab.
        this.selectedTab = `${this.tabsAutoId}-tab-0`;
        this.selectedTabName = SummaryPageTabEnum.productList;
        this.numberOfCustomTabs = 0;
    }
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html ` <style>
				${SummaryPageTabsStyle.getStyle({
            theme: this.theme,
            hideSkapaTabsOnPrint: !!this.contentToPrint
        })}
			</style>
			${this.getTabs()}${this.getContentToPrint()}`;
    }
    async connectedCallback() {
        registerIcon(warningTriangle);
        registerIcon(informationCircle);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-tabs', SkapaTabs);
        this.customElements.define('skapa-image', SkapaImage);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('kompis-summary-page-product-list', SummaryPageProductList);
        this.customElements.define('kompis-summary-page-images', SummaryPageImages);
        this.customElements.define('kompis-summary-page-security', SummaryPageSecurity);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('customTabButtons')) {
            this.numberOfCustomTabs = SummaryPageCustomTabsUtility.getNumberOfCustomTabsToShow(this.customTabButtons);
        }
        return true;
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    showImagesTab() {
        return !!this.images?.length;
    }
    showSecurityTab() {
        return !!this.securityInformation;
    }
    getTabName(index) {
        const tabsInOrder = [SummaryPageTabEnum.productList];
        if (this.showImagesTab()) {
            tabsInOrder.push(SummaryPageTabEnum.images);
        }
        if (this.showSecurityTab()) {
            tabsInOrder.push(SummaryPageTabEnum.security);
        }
        for (let n = 1; n <= this.numberOfCustomTabs; n++) {
            tabsInOrder.push(SummaryPageTabEnum[`customTab${n}`]);
        }
        return tabsInOrder[index];
    }
    getCustomTabs() {
        const tabs = [];
        for (let n = 1; n <= this.numberOfCustomTabs; n++) {
            tabs.push(html `
				<button>${this.customTabButtons?.[n - 1]?.label}</button>
				<div slot="panels">
					<slot slot="custom-tab-${n}" name="custom-tab-${n}"></slot>
				</div>
			`);
        }
        return tabs;
    }
    getTabs() {
        return html ` <skapa-tabs
			auto-id="${this.tabsAutoId}"
			active-tab="${this.selectedTab}"
			@skapa-tab-select="${({ detail: targetIndex }) => {
            this.selectedTab = `${this.tabsAutoId}-tab-${targetIndex}`;
            this.selectedTabName = this.getTabName(targetIndex);
            const tabName = this.getTabName(targetIndex);
            this.dispatchEvent(new CustomEvent('tabChange', {
                bubbles: true,
                detail: { selectedTab: tabName }
            }));
            this.requestUpdate();
        }}"
		>
			<button>${this.getTranslations().summaryPageProductListTab}</button>
			<div slot="panels">
				${this.getProductListTab({
            pickingListPrintLayout: ProductRowLayoutEnum.default,
            defaultSortBy: this.defaultSortBy,
            withModal: true
        })}
			</div>
			${this.showImagesTab()
            ? html `
						<button>${this.getTranslations().summaryPageImagesTab}</button>
						<div slot="panels">${this.getImagesTab()}</div>
					`
            : ''}
			${this.showSecurityTab()
            ? html `
						<button class="images">
							<skapa-icon slot="icon" icon="warning-triangle"></skapa-icon>
							${this.getTranslations().summaryPageSecurityTab}
						</button>
						<div slot="panels">${this.getSecurityTab()}</div>
					`
            : ''}
			${this.getCustomTabs()}
		</skapa-tabs>`;
    }
    getProductListTab(options) {
        return html ` <kompis-summary-page-product-list
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.shoppingProducts="${this.shoppingProducts}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.stores="${this.stores}"
			.storeId="${this.storeId}"
			.productMeasures="${this.productMeasures}"
			.visibleModal="${options.withModal ? this.visibleModal : null}"
			.disableLinks="${this.disableLinks}"
			.homeDelivery="${this.homeDelivery}"
			.pickingListPrintLayout="${options.pickingListPrintLayout}"
			.includedProducts="${this.includedProducts}"
			.collectableProducts="${this.collectableProducts}"
			.collectedProducts="${this.collectedProducts}"
			.showDesignText="${this.showDesignText}"
			.productListSections="${this.productListSections}"
			.productListAddable="${this.productListAddable}"
			.productListRemovable="${this.productListRemovable}"
			.defaultSortBy="${options.defaultSortBy}"
			.startHeadingsAtH2="${this.startHeadingsAtH2}"
			.zipCode="${this.zipCode}"
			.measurementSystem="${this.measurementSystem}"
			@modalOpen="${(event) => {
            this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail: event.detail }));
        }}"
			@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
			@storeSelectorChange="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('storeSelectorChange', { bubbles: true, detail }));
        }}"
			@sortListChange="${({ detail }) => this.dispatchEvent(new CustomEvent('sortListChange', {
            bubbles: true,
            detail
        }))}"
			@homeDeliveryClick="${(event) => {
            this.dispatchEvent(new CustomEvent('homeDeliveryClick', { bubbles: true, detail: event.detail }));
        }}"
			@collectProduct="${(event) => {
            this.dispatchEvent(new CustomEvent('collectProduct', { bubbles: true, detail: event.detail }));
        }}"
			@productListAdd="${(event) => {
            this.dispatchEvent(new CustomEvent('productListAdd', { bubbles: true, detail: event.detail }));
        }}"
			@productListRemove="${(event) => {
            this.dispatchEvent(new CustomEvent('productListRemove', { bubbles: true, detail: event.detail }));
        }}"
			@productListExpand="${(event) => {
            this.dispatchEvent(new CustomEvent('productListExpand', { bubbles: true, detail: event.detail }));
        }}"
		>
		</kompis-summary-page-product-list>`;
    }
    getImagesTab() {
        return this.selectedTabName === SummaryPageTabEnum.images
            ? html `<kompis-summary-page-images
					.theme="${this.theme}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.images="${this.images}"
					@change="${({ detail }) => {
                this.dispatchEvent(new CustomEvent('selectedImageIndexChange', {
                    bubbles: true,
                    detail: { selectedIndex: detail.selectedIndex }
                }));
            }}"
				></kompis-summary-page-images>`
            : null;
    }
    getSecurityTab() {
        if (!this.securityInformation) {
            return null;
        }
        return html `<kompis-summary-page-security
			.theme="${this.theme}"
			.title="${this.securityInformation.title}"
			.description="${this.securityInformation.description}"
			.imageSrc="${this.securityInformation.imageSrc}"
			.imageAlt="${this.securityInformation.imageAlt}"
			.startHeadingsAtH3="${this.startHeadingsAtH2}"
		>
		</kompis-summary-page-security>`;
    }
    getContentToPrint() {
        if (!this.contentToPrint?.length) {
            return null;
        }
        const uniqueContentToPrint = this.contentToPrint.filter((contentToPrint, index, array) => array.indexOf(contentToPrint) === index);
        return html `<div class="contentToPrint">
			${uniqueContentToPrint.map((contentToPrint) => this.getContentToPrintSection(contentToPrint))}
		</div>`;
    }
    getContentToPrintSection(printableContent) {
        const header = html `<h2 class="${Text.classnames({ heading: { m: true } })}">
			${this.getHeaderText(printableContent)}
		</h2>`;
        switch (printableContent) {
            case PrintableContentEnum.productListTab:
                return html `<section>
					${header}${this.getSubHeader()}${this.getProductListTab({
                    pickingListPrintLayout: this.pickingListPrintLayout,
                    defaultSortBy: SortListOptionsEnum.aisleLocation
                })}
				</section>`;
            case PrintableContentEnum.imagesTab:
                return this.showImagesTab()
                    ? html `<section class="avoidBreak">${header}${this.getImageGrid()}</section>`
                    : null;
            case PrintableContentEnum.securityTab:
                return this.showSecurityTab()
                    ? html `<section>${header}${this.getSecurityTab()}</section>`
                    : null;
            case PrintableContentEnum.customPrintContent:
                return html `<section><slot name="custom-print-content"></slot></section>`;
        }
    }
    getHeaderText(printableContent) {
        switch (printableContent) {
            case PrintableContentEnum.productListTab:
                return this.getTranslations().summaryPageProductListTab;
            case PrintableContentEnum.imagesTab:
                return this.getTranslations().summaryPageImagesTab;
            case PrintableContentEnum.securityTab:
                return this.getTranslations().summaryPageSecurityTab;
            default:
                return null;
        }
    }
    getSubHeader() {
        const template = [
            html `<h4 class="subheader text--heading-s">${this.getTranslations().pickingListHeader}</h4>`
        ];
        if (this.pickingListPrintLayout === ProductRowLayoutEnum.compactPrint) {
            const informativeVariant = 'informative';
            template.push(html `<skapa-inline-message subtle class="message" variant="${informativeVariant}">
					<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
					<span slot="body"
						>${this.getTranslations().summaryPagePrintStockAvailabilityDisclaimer}</span
					>
				</skapa-inline-message>`);
        }
        template.push();
        return template;
    }
    getImageGrid() {
        if (this.images?.length === 1) {
            return html `<skapa-image ?fallback="${null}">
				<img src="${this.images[0].src}" alt="" />
			</skapa-image>`;
        }
        return html `<div class="imageGrid">
			${this.images?.map(({ src }) => html `
					<skapa-image ?fallback="${null}">
						<img src="${src}" alt="" />
					</skapa-image>
				`)}
		</div>`;
    }
}
__decorate([
    property({ type: Object, required: true })
], SummaryPageTabs.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "settings", void 0);
__decorate([
    property({ type: Array, required: true })
], SummaryPageTabs.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "storeAvailabilities", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "stores", void 0);
__decorate([
    property({ type: String })
], SummaryPageTabs.prototype, "storeId", void 0);
__decorate([
    property({ type: String })
], SummaryPageTabs.prototype, "productMeasures", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageTabs.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "images", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "securityInformation", void 0);
__decorate([
    property({ type: Object, synchronous: true })
], SummaryPageTabs.prototype, "homeDelivery", void 0);
__decorate([
    property({ type: String })
], SummaryPageTabs.prototype, "zipCode", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageTabs.prototype, "collectableProducts", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "collectedProducts", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageTabs.prototype, "showDesignText", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "productListSections", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "customTabButtons", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageTabs.prototype, "startHeadingsAtH2", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "includedProducts", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "productListAddable", void 0);
__decorate([
    property({ type: Array })
], SummaryPageTabs.prototype, "productListRemovable", void 0);
__decorate([
    property({ type: String })
], SummaryPageTabs.prototype, "defaultSortBy", void 0);
__decorate([
    property({ type: Object })
], SummaryPageTabs.prototype, "contentToPrint", void 0);
__decorate([
    property({ type: String })
], SummaryPageTabs.prototype, "pickingListPrintLayout", void 0);
__decorate([
    property({ type: String })
], SummaryPageTabs.prototype, "measurementSystem", void 0);
//# sourceMappingURL=SummaryPageTabs.js.map